import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  ListGroupButtonItem,
  ListGroupNavItem,
  SidebarNavBack,
  SidebarNavSection,
} from './ListGroupNavComponents';
import { useAllIncidents, useIsEscalated } from '../IAP/hooks/incidentHooks';
import { updateCurrentIncident } from '../../actions/diceActions';
import { useDispatch } from 'react-redux';
import { useIncident } from '../IAP/hooks/incidentHooks';
import {
  useHwcgFlag,
  useStreamlinePlatformFlag,
  useUscgFocRequirementsFlag,
} from '../../utils/featureFlags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { useActiveSeatNav } from 'components/IAP/positionWorkspace/useActiveSeats';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';
import { getIncidentWorkspaceNavItems } from './incidentWorkspaceNavItems';
import { filterNavItemsByQuery } from './sidebarNavSearch';
import { SidebarNavSearchInput } from './SidebarNavSearchInput';

const IncidentWorkspacePanel: React.FC<{
  navBack: () => void;
}> = ({ navBack }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [workspaceQuery, setWorkspaceQuery] = useState('');
  const [navQuery, setNavQuery] = useState('');
  const location = useLocation();
  const [workspacesOpen, setWorkspacesOpen] = useState(() =>
    location.pathname.includes('/seat/')
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const streamlinePlatform = useStreamlinePlatformFlag();
  const hwcg = useHwcgFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  const mapCtx = useOptionalUscgMapContext();

  const { data: incident } = useIncident();
  const { data: allIncidents } = useAllIncidents();
  const isEscalated = useIsEscalated();
  const { seats, held } = useActiveSeatNav();

  const showMapForTab = () => {
    mapCtx?.setIsMapVisible(true);
  };
  const myWorkspacePath =
    held.kind === 'one'
      ? `/iap/${incident?.id}/seat/${held.slug}`
      : held.kind === 'many'
        ? `/iap/${incident?.id}`
        : null;

  const incidentChangeHandler = (incidentId: string, isNew: boolean) => {
    const currentIncidentId = String(incident?.id);
    const newIncidentId = String(incidentId);

    if (!isNew && currentIncidentId === newIncidentId) {
      return;
    }
    if (!allIncidents?.length) {
      return;
    }
    const selectedIncident = allIncidents.find(
      (row) => String(row?.id) === newIncidentId
    );
    if (!selectedIncident?.id) {
      return;
    }
    const newPath = location.pathname.replace(
      `/iap/${currentIncidentId || 'undefined'}`,
      `/iap/${selectedIncident.id}`
    );

    const targetPath = newPath.includes('/iap/')
      ? newPath
      : `/iap/${selectedIncident.id}/dashboard`;

    dispatch(updateCurrentIncident(selectedIncident));

    const newUrl = location.search
      ? `${targetPath}${location.search}`
      : targetPath;

    navigate(newUrl);
    setShowDropdown(false);
  };

  const workspaceNeedle = workspaceQuery.trim().toLowerCase();
  const visibleSeats = workspaceNeedle
    ? seats.filter((seat) => seat.label.toLowerCase().includes(workspaceNeedle))
    : seats;

  const workspaceOptions =
    allIncidents
      ?.filter((row) => row.archived === 0)
      .map((row) => ({
        label: row.name,
        value: String(row.id),
      })) ?? [];

  const navItems = useMemo(() => {
    if (!incident?.id) return [];
    return getIncidentWorkspaceNavItems({
      incidentId: String(incident.id),
      isEscalated,
      myWorkspacePath,
      hwcg,
      streamlinePlatform,
      showApplyPlan: hwcg || uscgFoc,
    });
  }, [
    incident?.id,
    isEscalated,
    myWorkspacePath,
    hwcg,
    streamlinePlatform,
    uscgFoc,
  ]);

  const visibleNavItems = useMemo(
    () => filterNavItemsByQuery(navItems, navQuery),
    [navItems, navQuery]
  );
  const searching = navQuery.trim().length > 0;

  const handleBack = () => {
    setNavQuery('');
    navBack();
  };

  const zeroStateListGroup = (
    <SidebarNavSection title="Manage Incident">
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="checklist"
        label="Planning"
      />
      <ListGroupNavItem
        isDisabled={true}
        showArrow={true}
        to={`/none`}
        icon="commit"
        label="Operations"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="file_present"
        label="Documents"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="bar_chart"
        label="Analytics"
      />
      <ListGroupNavItem
        isDisabled={true}
        to={`/none`}
        icon="settings"
        label="Settings"
      />
    </SidebarNavSection>
  );

  const defaultListGroup = (
    <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-2">
      {!searching && seats.length > 0 ? (
        <div data-tour="position-workspaces-nav">
          <ListGroupButtonItem
            onClick={() => setWorkspacesOpen((open) => !open)}
            open={workspacesOpen}
            icon="workspaces"
            label="Position Workspaces"
          />
        </div>
      ) : null}
      {!searching && workspacesOpen && seats.length > 0 ? (
        <>
          <Input
            aria-label="Search position workspaces"
            placeholder="Search position workspaces"
            value={workspaceQuery}
            onChange={(event) => setWorkspaceQuery(event.target.value)}
            className="tw:my-1 tw:ml-5 tw:h-8 tw:w-auto"
          />
          {visibleSeats.map((seat) => (
            <ListGroupNavItem
              key={seat.slug}
              to={`/iap/${incident?.id}/seat/${seat.slug}`}
              label={seat.label}
              secondary
            />
          ))}
          {workspaceNeedle && visibleSeats.length === 0 ? (
            <p className="tw:px-8 tw:py-1 tw:text-sm tw:text-muted-foreground">
              No matching workspaces
            </p>
          ) : null}
        </>
      ) : null}
      {visibleNavItems.length === 0 ? (
        <p className="tw:px-3 tw:py-2 tw:text-xs tw:text-muted-foreground">
          No matching navigation
        </p>
      ) : (
        visibleNavItems.map((item) => (
          <ListGroupNavItem
            key={item.id}
            to={item.to}
            icon={item.icon}
            label={item.label}
            end={item.end}
            onClick={item.opensMapTab ? showMapForTab : undefined}
          />
        ))
      )}
    </div>
  );

  return (
    <nav
      aria-label="secondary sidebar"
      className="second-panel tw:flex tw:flex-col tw:gap-1"
    >
      <SidebarNavBack
        title="Incident Workspace"
        onBack={handleBack}
        icon="electric_bolt"
      />
      <div className="tw:px-4 tw:text-base tw:font-medium">
        {showDropdown || incident?.id === undefined ? (
          <SearchableSelect
            aria-label="Select an Incident Workspace"
            placeholder="Select an Incident Workspace"
            options={workspaceOptions}
            value={
              workspaceOptions.find(
                (option) => option.value === String(incident?.id)
              ) ?? null
            }
            onChange={(next) => {
              if (!next) return;
              incidentChangeHandler(next.value, false);
            }}
            positionerClassName="tw:z-[1100]"
            itemClassName="tw:h-auto tw:min-h-8 tw:items-start tw:whitespace-normal tw:rounded-none tw:border-b tw:border-border tw:py-1.5 tw:last:border-b-0"
          />
        ) : (
          incident?.name
        )}
      </div>
      {incident?.id !== undefined && (
        <div className="tw:px-4">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="tw:h-auto tw:px-0"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            Switch Workspace
          </Button>
        </div>
      )}
      {incident?.id !== undefined ? (
        <SidebarNavSearchInput value={navQuery} onChange={setNavQuery} />
      ) : null}
      {incident?.id === undefined ? zeroStateListGroup : defaultListGroup}
    </nav>
  );
};

export default IncidentWorkspacePanel;
